<div data-spartan-page class="p-6 md:p-8 space-y-5"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <app-page-header
    eyebrow="Admin"
    title="User Management"
    subtitle="Identity, access and sessions for the RARR platform."
  >
    <div actions class="flex items-center gap-2">
      <button hlmBtn variant="outline" size="sm" class="gap-2" (click)="loadUsers()" [disabled]="isLoading">
        @if (isLoading) {
          <span class="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        }
        {{ isLoading ? 'Refreshing...' : 'Refresh' }}
      </button>
      <button hlmBtn size="sm" class="gap-2" (click)="openCreateUserModal()">
        <ng-icon hlm name="lucidePlus" class="size-4" />
        Add User
      </button>
    </div>
  </app-page-header>

  <!-- Inline alerts -->
  @if (successMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-500/15 dark:border-green-500/30 p-3.5 text-sm text-green-900 dark:text-green-300">
      <span class="shrink-0 font-medium">Success:</span>
      <span class="flex-1">{{ successMessage }}</span>
      <button type="button" class="shrink-0 text-green-700 dark:text-green-400 hover:text-green-900 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded"
        (click)="clearMessages()" aria-label="Dismiss">
        <ng-icon hlm name="lucideX" class="size-4" />
      </button>
    </div>
  }
  @if (errorMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/15 dark:border-red-500/30 p-3.5 text-sm text-red-900 dark:text-red-300">
      <span class="shrink-0 font-medium">Error:</span>
      <span class="flex-1">{{ errorMessage }}</span>
      <button type="button" class="shrink-0 text-red-700 dark:text-red-400 hover:text-red-900 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded"
        (click)="clearMessages()" aria-label="Dismiss">
        <ng-icon hlm name="lucideX" class="size-4" />
      </button>
    </div>
  }

  <!-- Stats strip -->
  <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
    <div hlmCard class="p-4 flex items-center gap-3">
      <span class="grid place-items-center w-9 h-9 rounded-md bg-accent-light text-accent shrink-0">
        <ng-icon hlm name="lucideUsers" class="size-4" />
      </span>
      <div>
        <div class="text-xl font-semibold tabular-nums tracking-tight">{{ users.length }}</div>
        <div class="text-[11px] text-muted-foreground">Total</div>
      </div>
    </div>
    <div hlmCard class="p-4 flex items-center gap-3">
      <span class="grid place-items-center w-9 h-9 rounded-md bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400 shrink-0">
        <ng-icon hlm name="lucideShield" class="size-4" />
      </span>
      <div>
        <div class="text-xl font-semibold tabular-nums tracking-tight">{{ getAdminCount() }}</div>
        <div class="text-[11px] text-muted-foreground">Admins</div>
      </div>
    </div>
    <div hlmCard class="p-4 flex items-center gap-3">
      <span class="grid place-items-center w-9 h-9 rounded-md bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 shrink-0">
        <ng-icon hlm name="lucideShoppingBag" class="size-4" />
      </span>
      <div>
        <div class="text-xl font-semibold tabular-nums tracking-tight">{{ getShopeeDocsCount() }}</div>
        <div class="text-[11px] text-muted-foreground">Shopee Docs</div>
      </div>
    </div>
    <div hlmCard class="p-4 flex items-center gap-3">
      <span class="grid place-items-center w-9 h-9 rounded-md bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 shrink-0">
        <ng-icon hlm name="lucideCheckSquare" class="size-4" />
      </span>
      <div>
        <div class="text-xl font-semibold tabular-nums tracking-tight">{{ getShopeeCheckerCount() }}</div>
        <div class="text-[11px] text-muted-foreground">Checkers</div>
      </div>
    </div>
    <div hlmCard class="p-4 flex items-center gap-3">
      <span class="grid place-items-center w-9 h-9 rounded-md bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300 shrink-0">
        <ng-icon hlm name="lucideUser" class="size-4" />
      </span>
      <div>
        <div class="text-xl font-semibold tabular-nums tracking-tight">{{ getActiveUsersCount() }}</div>
        <div class="text-[11px] text-muted-foreground">Regular</div>
      </div>
    </div>
    <div hlmCard class="p-4 flex items-center gap-3">
      <span class="grid place-items-center w-9 h-9 rounded-md bg-accent-light text-accent shrink-0">
        <ng-icon hlm name="lucideUserCog" class="size-4" />
      </span>
      <div>
        <div class="text-xl font-semibold tabular-nums tracking-tight">{{ getForwardingAdminCount() }}</div>
        <div class="text-[11px] text-muted-foreground">Fwd Admin</div>
      </div>
    </div>
    <div hlmCard class="p-4 flex items-center gap-3">
      <span class="grid place-items-center w-9 h-9 rounded-md bg-accent-light text-accent shrink-0">
        <ng-icon hlm name="lucideBriefcase" class="size-4" />
      </span>
      <div>
        <div class="text-xl font-semibold tabular-nums tracking-tight">{{ getForwardingUserCount() }}</div>
        <div class="text-[11px] text-muted-foreground">Fwd User</div>
      </div>
    </div>
  </div>

  <!-- Filter card -->
  <div hlmCard>
    <div class="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
      <!-- Search input -->
      <div class="flex-1 min-w-[220px] max-w-sm relative">
        <ng-icon hlm name="lucideSearch" class="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          class="w-full h-9 rounded-md border border-border-strong bg-surface text-sm pl-8 pr-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent transition"
          placeholder="Search by name, username, or email…"
          [(ngModel)]="searchTerm"
          (input)="applyFilters()"
          aria-label="Search users"
        />
      </div>
      <!-- Role filter -->
      <div class="relative">
        <select
          class="appearance-none h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition w-44"
          [(ngModel)]="selectedRole"
          (change)="applyFilters()"
          aria-label="Filter by role"
        >
          <option value="">All Roles</option>
          @for (role of availableRoles; track role.key) {
            <option [value]="role.key">{{ role.name }}</option>
          }
        </select>
        <ng-icon hlm name="lucideChevronDown" class="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
      </div>
      <button hlmBtn variant="outline" size="sm" (click)="clearFilters()">Clear</button>

      <div class="flex-1"></div>
      <!-- Table count + export -->
      <span class="text-sm text-muted-foreground">{{ totalCount > 0 ? totalCount : filteredUsers.length }} users</span>
      <button hlmBtn variant="outline" size="sm" class="gap-1.5" (click)="exportUsers()">
        <ng-icon hlm name="lucideDownload" class="size-4" />
        Export
      </button>
    </div>

    <!-- Loading -->
    @if (isLoading) {
      <div class="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
        <span class="inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        <span class="text-sm">Loading users…</span>
      </div>
    }

    <!-- Empty — no users at all -->
    @if (!isLoading && users.length === 0) {
      <div class="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
        <p class="text-base font-medium">No Users Found</p>
        <p class="text-sm">Start by creating your first user.</p>
        <button hlmBtn size="sm" class="gap-2" (click)="openCreateUserModal()">Create First User</button>
      </div>
    }

    <!-- No search results -->
    @if (!isLoading && users.length > 0 && filteredUsers.length === 0) {
      <div class="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
        <p class="text-base font-medium">No Results Found</p>
        <p class="text-sm">Try adjusting your search criteria.</p>
        <button hlmBtn variant="outline" size="sm" (click)="clearFilters()">Clear Filters</button>
      </div>
    }

    <!-- Table -->
    @if (!isLoading && filteredUsers.length > 0) {
      <div class="overflow-x-auto">
        <table class="w-full text-sm" aria-label="Users table">
          <thead>
            <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
              <th class="px-4 py-2.5 text-left font-medium">User</th>
              <th class="px-4 py-2.5 text-left font-medium">Contact</th>
              <th class="px-4 py-2.5 text-left font-medium">Role</th>
              <th class="px-4 py-2.5 text-left font-medium w-36"></th>
            </tr>
          </thead>
          <tbody>
            @for (user of filteredUsers; track user.id) {
              <tr class="border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors h-12">
                <td class="px-4">
                  <div class="flex items-center gap-3">
                    <span class="grid place-items-center w-8 h-8 rounded-full bg-accent-light text-accent text-[11px] font-semibold shrink-0">
                      {{ getUserInitials(user) }}
                    </span>
                    <div class="min-w-0">
                      <p class="font-medium leading-tight truncate">
                        {{ user.firstName }} {{ user.lastName }}
                        @if (!user.isActive) {
                          <span hlmBadge class="ml-1.5 text-[10px] bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-500/30">Locked</span>
                        }
                      </p>
                      <p class="text-[11px] text-muted-foreground font-mono">&#64;{{ user.username }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-4">
                  <p class="truncate max-w-[200px]">{{ user.email }}</p>
                  <p class="text-[10px] text-muted-foreground font-mono">ID: {{ user.id }}</p>
                </td>
                <td class="px-4">
                  <span hlmBadge [class]="getRoleBadgeClass(user.role)">
                    {{ getRoleDisplayName(user.role) }}
                  </span>
                </td>
                <td class="px-4">
                  <div class="flex items-center justify-end gap-1">
                    <button hlmBtn variant="ghost" size="sm"
                      class="h-8 w-8 p-0 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded-md"
                      (click)="viewUserSecurityProfile(user)" title="Security Profile" aria-label="Security Profile">
                      <ng-icon hlm name="lucideShield" class="size-4" />
                    </button>
                    <button hlmBtn variant="ghost" size="sm"
                      class="h-8 w-8 p-0 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded-md"
                      (click)="editUser(user)" title="Edit" aria-label="Edit user">
                      <ng-icon hlm name="lucidePencil" class="size-4" />
                    </button>
                    @if (user.isActive) {
                      <button hlmBtn variant="ghost" size="sm"
                        class="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded-md"
                        (click)="lockUser(user)" title="Lock Account" aria-label="Lock account">
                        <ng-icon hlm name="lucideLock" class="size-4" />
                      </button>
                    }
                    @if (!user.isActive) {
                      <button hlmBtn variant="ghost" size="sm"
                        class="h-8 w-8 p-0 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded-md"
                        (click)="unlockUser(user)" title="Unlock Account" aria-label="Unlock account">
                        <ng-icon hlm name="lucideUnlock" class="size-4" />
                      </button>
                    }
                    <button hlmBtn variant="ghost" size="sm"
                      class="h-8 w-8 p-0 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded-md"
                      (click)="confirmDeleteUser(user)" title="Delete" aria-label="Delete user">
                      <ng-icon hlm name="lucideTrash2" class="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    <!-- Pagination -->
    @if (!isLoading && totalPages > 1) {
      <div class="px-4 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Showing {{ ((currentPage - 1) * pageSize) + 1 }}–{{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} users
        </span>
        <div class="flex items-center gap-1">
          <button hlmBtn variant="outline" size="sm" class="h-7 w-7 p-0" (click)="previousPage()" [disabled]="currentPage === 1" aria-label="Previous page">&#x2039;</button>
          @for (page of getPageNumbers(); track page) {
            <button
              hlmBtn
              size="sm"
              class="h-7 w-7 p-0"
              [class.bg-accent]="page === currentPage"
              [class.text-white]="page === currentPage"
              [class.border-accent]="page === currentPage"
              variant="outline"
              (click)="loadPage(page)"
            >{{ page }}</button>
          }
          <button hlmBtn variant="outline" size="sm" class="h-7 w-7 p-0" (click)="nextPage()" [disabled]="currentPage === totalPages" aria-label="Next page">&#x203A;</button>
        </div>
      </div>
    }
  </div>
</div>

<!-- User Modal -->
@if (showUserModal) {
  <div class="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true" [attr.aria-label]="isEditMode ? 'Edit User' : 'Create User'">
    <div class="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" (click)="closeUserModal()"></div>
    <div class="relative w-full max-w-xl bg-surface rounded-xl border border-border shadow-xl">
      <div class="px-6 pt-5 pb-4 border-b border-border flex items-center justify-between">
        <div>
          <h2 class="text-base font-semibold">{{ isEditMode ? 'Edit User' : 'Create New User' }}</h2>
          <p class="text-xs text-muted-foreground mt-0.5">{{ isEditMode ? 'Update profile and role assignment.' : 'Fill in details to create a new user.' }}</p>
        </div>
        <button type="button" class="h-8 w-8 grid place-items-center rounded-md hover:bg-surface-raised text-muted-foreground appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent"
          (click)="closeUserModal()" aria-label="Close">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>
      <form #userForm="ngForm" (ngSubmit)="saveUser()">
        <div class="px-6 py-5 space-y-4">
          @if (errorMessage) {
            <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/15 p-3.5 text-sm text-red-900 dark:text-red-300">
              <span class="flex-1">{{ errorMessage }}</span>
            </div>
          }
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">First Name <span class="text-red-500">*</span></label>
              <input type="text"
                class="w-full h-9 rounded-md border border-border-strong bg-surface text-sm px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                [(ngModel)]="currentUser.firstName" name="firstName" required #firstName="ngModel">
              @if (firstName.invalid && firstName.touched) {
                <p class="text-xs text-red-600 mt-1">First name is required</p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">Last Name <span class="text-red-500">*</span></label>
              <input type="text"
                class="w-full h-9 rounded-md border border-border-strong bg-surface text-sm px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                [(ngModel)]="currentUser.lastName" name="lastName" required #lastName="ngModel">
              @if (lastName.invalid && lastName.touched) {
                <p class="text-xs text-red-600 mt-1">Last name is required</p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">Username <span class="text-red-500">*</span></label>
              <input type="text"
                class="w-full h-9 rounded-md border border-border-strong bg-surface text-sm px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                [(ngModel)]="currentUser.username" name="username" required #username="ngModel">
              @if (username.invalid && username.touched) {
                <p class="text-xs text-red-600 mt-1">Username is required</p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">Email <span class="text-red-500">*</span></label>
              <input type="email"
                class="w-full h-9 rounded-md border border-border-strong bg-surface text-sm px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                [(ngModel)]="currentUser.email" name="email" required email #email="ngModel">
              @if (email.invalid && email.touched) {
                <p class="text-xs text-red-600 mt-1">Valid email is required</p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">Role <span class="text-red-500">*</span></label>
              <div class="relative">
                <select
                  class="appearance-none w-full h-9 rounded-md border border-border-strong bg-surface text-sm px-3 pr-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                  [(ngModel)]="currentUser.role" name="role" required #role="ngModel">
                  <option value="">Select Role</option>
                  @for (r of availableRoles; track r.key) {
                    <option [value]="r.key">{{ r.name }}</option>
                  }
                </select>
                <ng-icon hlm name="lucideChevronDown" class="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
              </div>
              @if (role.invalid && role.touched) {
                <p class="text-xs text-red-600 mt-1">Role is required</p>
              }
            </div>
            @if (!isEditMode || showPasswordField) {
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">
                  Password @if (!isEditMode) { <span class="text-red-500">*</span> }
                </label>
                <div class="flex gap-1">
                  <input [type]="showPassword ? 'text' : 'password'"
                    class="flex-1 h-9 rounded-md border border-border-strong bg-surface text-sm px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                    [(ngModel)]="currentUser.password" name="password" [required]="!isEditMode" #password="ngModel">
                  <button type="button" hlmBtn variant="outline" size="sm" class="h-9 w-9 p-0 shrink-0" (click)="togglePasswordVisibility()" aria-label="Toggle password visibility">
                    <ng-icon hlm [name]="showPassword ? 'lucideEyeOff' : 'lucideEye'" class="size-4" />
                  </button>
                </div>
                @if (password.invalid && password.touched) {
                  <p class="text-xs text-red-600 mt-1">Password is required</p>
                }
                @if (isEditMode) {
                  <p class="text-[11px] text-muted-foreground mt-1">Leave blank to keep current password</p>
                }
              </div>
            }
          </div>
          @if (isEditMode && !showPasswordField) {
            <button type="button" hlmBtn variant="outline" size="sm" class="gap-2" (click)="togglePasswordField()">
              Change Password
            </button>
          }
          @if (!isEditMode || showPasswordField) {
            <div class="rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 p-3.5 text-sm text-blue-900 dark:text-blue-300 space-y-2">
              <p class="font-medium">Password Requirements:</p>
              <ul class="list-inside list-disc space-y-0.5 text-[12.5px]">
                <li>Minimum 8 characters</li>
                <li><strong>At least 1 uppercase letter (A-Z)</strong></li>
                <li>At least 1 digit (0-9)</li>
                <li>At least 1 lowercase letter (a-z)</li>
                <li class="text-muted-foreground">Special characters optional but recommended</li>
              </ul>
              <p class="text-[11px] text-blue-700 dark:text-blue-400">Example: <code class="font-mono bg-blue-100 dark:bg-blue-500/20 px-1 rounded">Fwdpassw0rd123!</code></p>
            </div>
          }
        </div>
        <div class="px-6 py-4 border-t border-border flex justify-end gap-2 bg-stone-50/50 dark:bg-stone-900/30 rounded-b-xl">
          <button type="button" hlmBtn variant="outline" size="sm" (click)="closeUserModal()">Cancel</button>
          <button type="submit" hlmBtn size="sm" [disabled]="userForm.invalid || isSubmitting" class="gap-2">
            @if (isSubmitting) {
              <span class="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            }
            {{ isEditMode ? 'Update User' : 'Create User' }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Delete Confirmation Modal -->
@if (showDeleteModal) {
  <div class="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true" aria-label="Confirm Delete">
    <div class="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" (click)="closeDeleteModal()"></div>
    <div class="relative w-full max-w-md bg-surface rounded-xl border border-border shadow-xl">
      <div class="px-6 pt-5 pb-4 border-b border-border flex items-center justify-between">
        <h2 class="text-base font-semibold text-red-700">Confirm Delete</h2>
        <button type="button" class="h-8 w-8 grid place-items-center rounded-md hover:bg-surface-raised text-muted-foreground appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent"
          (click)="closeDeleteModal()" aria-label="Close">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>
      <div class="px-6 py-5 space-y-3">
        @if (userToDelete) {
          <p class="text-sm">Are you sure you want to delete <strong>{{ userToDelete.firstName }} {{ userToDelete.lastName }}</strong>?</p>
        }
        <div class="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 p-3.5 text-sm text-amber-900 dark:text-amber-300">
          <strong>Warning:</strong> This action cannot be undone.
        </div>
      </div>
      <div class="px-6 py-4 border-t border-border flex justify-end gap-2 bg-stone-50/50 dark:bg-stone-900/30 rounded-b-xl">
        <button type="button" hlmBtn variant="outline" size="sm" (click)="closeDeleteModal()">Cancel</button>
        <button type="button" hlmBtn size="sm" class="bg-red-600 text-white hover:bg-red-500 gap-2" (click)="deleteUser()" [disabled]="isDeleting">
          @if (isDeleting) {
            <span class="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
          }
          Delete User
        </button>
      </div>
    </div>
  </div>
}

<!-- Security Profile Modal -->
@if (showSecurityModal) {
  <div class="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true" aria-label="Security Profile">
    <div class="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" (click)="closeSecurityModal()"></div>
    <div class="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-surface rounded-xl border border-border shadow-xl">
      <div class="px-6 pt-5 pb-4 border-b border-border flex items-center justify-between sticky top-0 bg-surface z-10">
        <h2 class="text-base font-semibold">
          Security Profile — {{ selectedUserForSecurity?.firstName }} {{ selectedUserForSecurity?.lastName }}
        </h2>
        <button type="button" class="h-8 w-8 grid place-items-center rounded-md hover:bg-surface-raised text-muted-foreground appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent"
          (click)="closeSecurityModal()" aria-label="Close">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>
      <div class="px-6 py-5">
        @if (isLoadingSecurityProfile) {
          <div class="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
            <span class="inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            <span class="text-sm">Loading security profile…</span>
          </div>
        }
        @if (!isLoadingSecurityProfile && userSecurityProfile) {
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">

            <!-- User Info -->
            <div hlmCard>
              <div class="px-5 pt-4 pb-3 border-b border-border">
                <p class="text-base font-semibold leading-tight">User Information</p>
              </div>
              <div class="p-5 space-y-3 text-sm">
                <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Full Name</p>
                  <p class="font-medium">{{ userSecurityProfile.user.firstName }} {{ userSecurityProfile.user.lastName }}</p></div>
                <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Username</p>
                  <p class="font-mono text-[12px]">{{ userSecurityProfile.user.username }}</p></div>
                <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Email</p>
                  <p>{{ userSecurityProfile.user.email || 'N/A' }}</p></div>
                <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Role</p>
                  <span hlmBadge [class]="getRoleBadgeClass(userSecurityProfile.user.role)">{{ getRoleDisplayName(userSecurityProfile.user.role) }}</span></div>
                <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Status</p>
                  @if (userSecurityProfile.user.isActive) {
                    <span hlmBadge class="bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300 ring-1 ring-green-200 dark:ring-green-500/30">Active</span>
                  } @else {
                    <span hlmBadge class="bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300 ring-1 ring-red-200 dark:ring-red-500/30">Locked</span>
                  }
                </div>
                <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Created</p>
                  <p class="text-[12px]">{{ userSecurityProfile.user.createdAtUtc | date:'medium' }}</p></div>
              </div>
            </div>

            <!-- Security Settings + Quick Actions -->
            <div class="space-y-4">
              <div hlmCard>
                <div class="px-5 pt-4 pb-3 border-b border-border">
                  <p class="text-base font-semibold leading-tight">Security Settings</p>
                </div>
                <div class="p-5 text-sm space-y-2">
                  @if (userSecurityProfile.securitySettings) {
                    <div>
                      <p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Failed Login Attempts</p>
                      <p class="font-semibold text-amber-700 dark:text-amber-400">{{ userSecurityProfile.securitySettings.failedLoginAttempts || 0 }}</p>
                    </div>
                    @if (userSecurityProfile.securitySettings.lastFailedLoginAtUtc) {
                      <div>
                        <p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Last Failed Login</p>
                        <p class="text-[12px]">{{ userSecurityProfile.securitySettings.lastFailedLoginAtUtc | date:'medium' }}</p>
                      </div>
                    }
                    @if (userSecurityProfile.securitySettings.lastSuccessfulLoginAtUtc) {
                      <div>
                        <p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Last Successful Login</p>
                        <p class="text-[12px]">{{ userSecurityProfile.securitySettings.lastSuccessfulLoginAtUtc | date:'medium' }}</p>
                      </div>
                    }
                    @if (userSecurityProfile.securitySettings.accountLockedUntilUtc) {
                      <div>
                        <p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Locked Until</p>
                        <p class="text-[12px] text-red-600 dark:text-red-400 font-medium">{{ userSecurityProfile.securitySettings.accountLockedUntilUtc | date:'medium' }}</p>
                      </div>
                    }
                  } @else {
                    <p class="text-muted-foreground text-xs">No security settings available</p>
                  }
                </div>
              </div>
              <div hlmCard>
                <div class="px-5 pt-4 pb-3 border-b border-border">
                  <p class="text-base font-semibold leading-tight">Quick Actions</p>
                </div>
                <div class="p-5 flex flex-col gap-2">
                  @if (userSecurityProfile.user.isActive) {
                    <button hlmBtn variant="outline" size="sm" class="justify-start gap-2 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/40 hover:bg-amber-50 dark:hover:bg-amber-500/10" (click)="lockUserFromProfile()">Lock Account</button>
                  } @else {
                    <button hlmBtn variant="outline" size="sm" class="justify-start gap-2 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/40 hover:bg-green-50 dark:hover:bg-green-500/10" (click)="unlockUserFromProfile()">Unlock Account</button>
                  }
                  <button hlmBtn variant="outline" size="sm" class="justify-start gap-2" (click)="invalidateAllSessionsForUser()">Invalidate All Sessions</button>
                </div>
              </div>
            </div>

            <!-- Active Sessions -->
            <div hlmCard>
              <div class="px-5 pt-4 pb-3 border-b border-border">
                <p class="text-base font-semibold leading-tight">Active Sessions ({{ userSecurityProfile.activeSessions.length }})</p>
              </div>
              <div class="p-5 space-y-3 max-h-72 overflow-y-auto">
                @if (userSecurityProfile.activeSessions.length === 0) {
                  <p class="text-xs text-muted-foreground">No active sessions</p>
                }
                @for (session of userSecurityProfile.activeSessions; track session.id) {
                  <div class="rounded-md border border-border p-3 text-xs space-y-2">
                    <div class="flex items-start justify-between gap-2">
                      <div>
                        <p class="font-mono text-[10.5px] text-muted-foreground">{{ session.id.substring(0, 12) }}…</p>
                        <span hlmBadge [class]="getSessionRiskBadgeClass(session)" class="mt-1">{{ getSessionRiskLevel(session) }} Risk</span>
                      </div>
                      <button hlmBtn variant="outline" size="sm" class="h-7 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/10 shrink-0" (click)="invalidateSpecificSession(session.id)">End</button>
                    </div>
                    <div class="grid grid-cols-2 gap-1.5">
                      <div><p class="text-[10px] text-muted-foreground">Device</p>
                        <p>{{ getDeviceTypeFromUserAgent(session.userAgent) }}</p></div>
                      <div><p class="text-[10px] text-muted-foreground">Browser</p>
                        <p>{{ getBrowserFromUserAgent(session.userAgent) }}</p></div>
                      <div><p class="text-[10px] text-muted-foreground">IP</p>
                        <p class="font-mono text-[10.5px]">{{ session.ipAddress }}</p></div>
                      <div><p class="text-[10px] text-muted-foreground">Last Active</p>
                        <p>{{ session.lastActivityAtUtc ? getTimeAgo(session.lastActivityAtUtc) : 'Unknown' }}</p></div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Recent Activities -->
          <div hlmCard class="mt-4">
            <div class="px-5 pt-4 pb-3 border-b border-border">
              <p class="text-base font-semibold leading-tight">Recent Activities (Last 20)</p>
            </div>
            <div class="p-5">
              @if (userSecurityProfile.recentActivities.length === 0) {
                <p class="text-xs text-muted-foreground">No recent activities found</p>
              }
              @if (userSecurityProfile.recentActivities.length > 0) {
                <div class="overflow-x-auto">
                  <table class="w-full text-xs" aria-label="Recent activities">
                    <thead>
                      <tr class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                        <th class="px-3 py-2 text-left font-medium">Time</th>
                        <th class="px-3 py-2 text-left font-medium">Action</th>
                        <th class="px-3 py-2 text-left font-medium">Resource</th>
                        <th class="px-3 py-2 text-left font-medium">IP</th>
                        <th class="px-3 py-2 text-left font-medium">Status</th>
                        <th class="px-3 py-2 text-left font-medium">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (activity of userSecurityProfile.recentActivities; track $index) {
                        <tr class="border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
                          <td class="px-3 h-9 font-mono text-[10.5px] text-muted-foreground">{{ activity.createdAtUtc | date:'short' }}</td>
                          <td class="px-3 h-9 font-medium">{{ activity.action }}</td>
                          <td class="px-3 h-9 text-muted-foreground">{{ activity.resource }}</td>
                          <td class="px-3 h-9 font-mono text-[10.5px]">{{ activity.ipAddress }}</td>
                          <td class="px-3 h-9">
                            @if (activity.success) {
                              <span hlmBadge class="bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300 ring-1 ring-green-200 dark:ring-green-500/30">Success</span>
                            } @else {
                              <span hlmBadge class="bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300 ring-1 ring-red-200 dark:ring-red-500/30">Failed</span>
                            }
                          </td>
                          <td class="px-3 h-9 text-muted-foreground">{{ activity.details || '—' }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </div>
        }
      </div>
      <div class="px-6 py-4 border-t border-border flex justify-end bg-stone-50/50 dark:bg-stone-900/30 rounded-b-xl">
        <button type="button" hlmBtn variant="outline" size="sm" (click)="closeSecurityModal()">Close</button>
      </div>
    </div>
  </div>
}
